import React, { createContext, useContext, useEffect, useState } from 'react';
import { WEB_COMPANIES } from '../constants/companies';

type Theme = 'light' | 'dark';
export type PreferredViewMode = 'grid' | 'list';

interface SettingsContextType {
    theme: Theme;
    toggleTheme: () => void;
    hiddenCompanies: string[];
    toggleCompanyVisibility: (company: string) => void;
    setCompanyGroupVisibility: (companies: string[], visible: boolean) => void;
    isSettingsOpen: boolean;
    setIsSettingsOpen: (isOpen: boolean) => void;
    appMode: boolean;
    setAppMode: (v: boolean) => void;
    verifyMode: 'camera' | 'manual';
    setVerifyMode: (m: 'camera' | 'manual') => void;
    preferredViewMode: PreferredViewMode;
    setPreferredViewMode: (mode: PreferredViewMode) => void;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const COMPANIES = WEB_COMPANIES.map((c) => c.id);

export function SettingsProvider({ children }: { children: React.ReactNode }) {
    // Theme State — light is the product default for new visitors. This must stay
    // in sync with the `className="light"` baked into the prerendered <html> in
    // `app/root.tsx` and
    // with the pre-paint script there; a mismatch reintroduces the theme flash.
    //
    // Read the saved choice in the initialiser, not in an effect: initialising to
    // 'light' and correcting afterwards makes the "Apply Theme" effect below write
    // `light` for one frame before the saved `dark` lands, which is the flash the
    // pre-paint script exists to prevent. During prerender there is no window, so
    // this returns 'light' and matches the baked class exactly.
    const [theme, setTheme] = useState<Theme>(() => {
        if (typeof window === 'undefined') return 'light';
        const saved = localStorage.getItem('theme');
        return saved === 'light' || saved === 'dark' ? saved : 'light';
    });

    // Hidden Companies State
    const [hiddenCompanies, setHiddenCompanies] = useState<string[]>([]);

    // App Mode — lean, app-like UI for returning users on phones (hides marketing copy)
    const [appMode, setAppMode] = useState(false);

    // Verify Mode (mobile) — 'camera' taps a logo straight into the camera, 'manual' opens number entry.
    //
    // Defaults to 'manual' (changed 2026-08-12). With 'camera' as the default, tapping a
    // provider fired a getUserMedia permission prompt the visitor never asked for, and an
    // uninvited prompt is denied far more often than one behind a button. 34% of users who
    // reached the camera hit an error, 67% of them on iOS — where Safari re-prompts on every
    // page load until the user allows it in Website Settings.
    //
    // Camera-first is still available: it is now opt-in via Settings, and users who already
    // chose it keep it, because localStorage is read below and wins over this default.
    const [verifyMode, setVerifyMode] = useState<'camera' | 'manual'>('manual');

    // Shared by History, Collections, Wishlist and Settings. Keeping this in
    // context makes a Settings change visible immediately in the current tab;
    // the browser `storage` event only fires in *other* tabs.
    const [preferredViewMode, setPreferredViewMode] = useState<PreferredViewMode>(() => {
        if (typeof window === 'undefined') return 'list';
        const saved = localStorage.getItem('preferredViewMode');
        return saved === 'list' || saved === 'grid' ? saved : 'list';
    });

    /*
     * `appMode` and `verifyMode` cannot read localStorage in their initialisers —
     * they drive markup, so that is a hydration mismatch — and load in the effect
     * below instead. Their persist effects run in the SAME commit as that load,
     * before its state update lands, so they used to write the *default* straight
     * over the saved value. React repaired it on the next render, but anything
     * that read the key in between (or a reload landing in that window) saw the
     * default, and in StrictMode the second pass re-read the value it had just
     * overwritten and made the loss permanent.
     *
     * This must be state, not a ref: a ref set at the end of the load effect is
     * already true when the persist effect runs in that same commit.
     */
    const [settingsLoaded, setSettingsLoaded] = useState(false);

    // Initialize from LocalStorage
    useEffect(() => {
        if (typeof window !== 'undefined') {
            // Theme is read in the useState initialiser above, deliberately not here.

            const savedHidden = localStorage.getItem('hiddenCompanies_v2');
            if (savedHidden) {
                setHiddenCompanies(JSON.parse(savedHidden));
            } else {
                // Default: Hide appOnly companies if no preference saved
                const defaultHidden = WEB_COMPANIES
                    .filter(c => c.appOnly)
                    .map(c => c.id);
                setHiddenCompanies(defaultHidden);
            }

            const savedAppMode = localStorage.getItem('appMode');
            if (savedAppMode) setAppMode(savedAppMode === 'true');

            const savedVerifyMode = localStorage.getItem('verifyMode');
            if (savedVerifyMode === 'camera' || savedVerifyMode === 'manual') setVerifyMode(savedVerifyMode);
        }
        setSettingsLoaded(true);
    }, []);

    // Persist Verify Mode
    useEffect(() => {
        if (!settingsLoaded) return;
        localStorage.setItem('verifyMode', verifyMode);
    }, [verifyMode, settingsLoaded]);

    useEffect(() => {
        localStorage.setItem('preferredViewMode', preferredViewMode);
    }, [preferredViewMode]);

    // Persist App Mode
    useEffect(() => {
        if (settingsLoaded) localStorage.setItem('appMode', String(appMode));
        if (typeof document !== 'undefined') {
            if (appMode) {
                document.body.classList.add('app-mode-active');
            } else {
                document.body.classList.remove('app-mode-active');
            }
        }
    }, [appMode, settingsLoaded]);

    // Apply Theme
    useEffect(() => {
        const root = window.document.documentElement;
        root.classList.remove('light', 'dark');
        root.classList.add(theme);
        localStorage.setItem('theme', theme);
    }, [theme]);

    // Persist Hidden Companies
    useEffect(() => {
        localStorage.setItem('hiddenCompanies_v2', JSON.stringify(hiddenCompanies));
    }, [hiddenCompanies]);

    const toggleTheme = () => {
        setTheme(prev => prev === 'light' ? 'dark' : 'light');
    };

    const toggleCompanyVisibility = (company: string) => {
        setHiddenCompanies(prev =>
            prev.includes(company)
                ? prev.filter(c => c !== company)
                : [...prev, company]
        );
    };

    /** Show or hide a whole group at once — the Settings pane's All / None. */
    const setCompanyGroupVisibility = (companies: string[], visible: boolean) => {
        setHiddenCompanies(prev =>
            visible
                ? prev.filter(c => !companies.includes(c))
                : Array.from(new Set([...prev, ...companies]))
        );
    };

    // Settings Modal State
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);

    return (
        <SettingsContext.Provider value={{
            theme,
            toggleTheme,
            hiddenCompanies,
            toggleCompanyVisibility,
            setCompanyGroupVisibility,
            isSettingsOpen,
            setIsSettingsOpen,
            appMode,
            setAppMode,
            verifyMode,
            setVerifyMode,
            preferredViewMode,
            setPreferredViewMode
        }}>
            {children}
        </SettingsContext.Provider>
    );
}

export function useSettings() {
    const context = useContext(SettingsContext);
    if (context === undefined) {
        throw new Error('useSettings must be used within a SettingsProvider');
    }
    return context;
}
